import type { IconButtonProps } from './types';

import React, { forwardRef } from 'react';

import Link from 'next/link';

import { button, spinner } from './styles';
import { Spinner } from '../../../atoms/Spinner';
import { Icon } from '../../../atoms/icons';

/**
 * IconButton component
 *
 * @param props - Component properties
 * @param props.iconName - Name of the icon to display
 * @param props.height - Height of the icon
 * @param props.width - Width of the icon
 * @param props.buttonType='brand' - Type of the button
 * @param props.disabled - Whether the button is disabled
 * @param props.onClick - Click event handler
 * @param props.className - Additional class names for the button
 * @param props.type - Button type attribute
 * @param props.loading=false - Whether the button shows a loading spinner
 * @param props.radius - Border radius of the button
 * @param props.space - Space around the button
 * @param props.size='m' - Size of the button
 * @param props.href - URL to navigate to when the button is clicked
 * @param props.iconColorAttr - svg icon color attribute
 *
 * @returns  The IconButton component
 */

const IconButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  IconButtonProps
>((props, ref) => {
  const {
    iconName,
    height,
    width,
    onClick,
    className,
    type,
    loading = false,
    disabled,
    buttonType = 'brand',
    radius,
    space,
    size = 'm',
    href,
    iconColorAttr = 'fill',
    ...rest
  } = props;

  const Content = () => (
    <>
      {loading ? (
        <Spinner width={width} height={height} className={spinner()} />
      ) : (
        <Icon iconName={iconName} height={height} width={width} />
      )}
    </>
  );

  if (href) {
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={href}
        className={button({
          buttonType,
          radius,
          space,
          disabled,
          size,
          iconColorAttr,
          class: className
        })}
        type={type}
        {...rest}
      >
        <Content />
      </Link>
    );
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      className={button({
        buttonType,
        radius,
        space,
        disabled,
        size,
        iconColorAttr,
        class: className
      })}
      onClick={onClick}
      disabled={disabled}
      type={type}
      {...rest}
    >
      <Content />
    </button>
  );
});

IconButton.displayName = 'IconButton';

export { IconButton };
export type { IconButtonProps };
